import React, { useEffect, useState } from "react";
import { Table, Modal, Input, Button, Upload, message, Select, Switch } from "antd";
import { BiEdit } from "react-icons/bi";
import { AiFillDelete } from "react-icons/ai";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import axios from "axios";
import { base_url } from "../utils/baseUrl";
import { useNavigate } from "react-router-dom";
const { TextArea } = Input;
const { Option } = Select;

const BlogList = () => {
    const [blogs, setBlogs] = useState([]);
    const [categories, setCategories] = useState([]);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedBlog, setSelectedBlog] = useState(null);
    const [updatedTitle, setUpdatedTitle] = useState("");
    const [updatedSlug, setUpdatedSlug] = useState("");
    const [updatedContent, setUpdatedContent] = useState("");
    const [updatedTags, setUpdatedTags] = useState("");
    const [updatedImage, setUpdatedImage] = useState(null);
    const [updatedCategoryId, setUpdatedCategoryId] = useState(null);
    const [updatedIsPublished, setUpdatedIsPublished] = useState(true);
    const [updatedAuthorName, setUpdatedAuthorName] = useState("");

    // Fetch blogs from backend
    useEffect(() => {
        const fetchBlogs = async () => {
            try {
                const response = await axios.get(`${base_url}/api/blogs/getBlog`);
                setBlogs(response.data.blogs);
                console.log(response);
            } catch (error) {
                console.error("Error fetching blogs:", error);
            }
        };

        fetchBlogs();
    }, []);

    // Fetch categories for dropdown (match Addblog.js)
    useEffect(() => {
        const fetchCategories = async () => {
            try {
                const response = await axios.get(`${base_url}/api/blogs/getBlogCategory`);
                setCategories(response.data.result || []);
            } catch (error) {
                console.error("Error fetching categories:", error);
            }
        };
        fetchCategories();
    }, []);

    // Debug useEffect to monitor content changes
    useEffect(() => {
        console.log("updatedContent changed:", updatedContent);
    }, [updatedContent]);

    const navigate = useNavigate();

    const adminToken = localStorage.getItem("adminToken");
    console.log("Admin Token:", adminToken);

    useEffect(() => {
        const adminToken = localStorage.getItem("adminToken");
        if (!adminToken) {
            navigate("/");
        }
    }, [navigate]);

    // Handle Edit Button Click
    const handleEdit = (blog) => {
        console.log("Editing blog:", blog); // Debug log
        console.log("Blog content:", blog.content); // Debug content specifically
        console.log("Blog content type:", typeof blog.content); // Debug content type

        setSelectedBlog(blog);
        setUpdatedTitle(blog.title || "");
        setUpdatedSlug(blog.slug || "");
        setUpdatedContent(blog.content || "");
        setUpdatedTags(blog.tags || "");
        setUpdatedImage(blog.thumbnail_url || null);
        setUpdatedCategoryId(blog.category_id || null);
        setUpdatedIsPublished(blog.is_published === "true" || blog.is_published === true);
        setUpdatedAuthorName(blog.author_name || "");
        setIsModalOpen(true);

        // Force ReactQuill to update after modal opens
        setTimeout(() => {
            console.log("Updated content after timeout:", blog.content);
            setUpdatedContent(blog.content || "");
        }, 100);
    };

    // Handle Save Changes
    const handleSaveChanges = async () => {
        if (!updatedTitle || !updatedSlug || !updatedContent || !updatedTags) {
            message.error("All fields are required.");
            return;
        }

        try {
            // Fix link formatting issues - remove unwanted line breaks after links but preserve user intent
            let cleanedContent = updatedContent;

            // Remove automatic <br> tags that React Quill adds after links (but preserve user-added ones)
            // Only remove <br> if it's immediately after a link with no user content
            cleanedContent = cleanedContent.replace(/<\/a>\s*<br\s*\/?>\s*(?=<span|<\/p>)/gi, '</a>');

            // Remove <br> tags that appear right after link spans (automatic ones)
            cleanedContent = cleanedContent.replace(/<\/span>\s*<br\s*\/?>\s*(?=<span|<\/p>)/gi, '</span>');

            // Ensure text flows properly after links but preserve intentional spacing
            cleanedContent = cleanedContent.replace(/<\/a>(\s*)<span/gi, '</a>$1<span');

            const formData = new FormData();
            formData.append("title", updatedTitle);
            formData.append("slug", updatedSlug);
            formData.append("content", cleanedContent); // Save cleaned content
            formData.append("tags", updatedTags);
            formData.append("category_id", updatedCategoryId);
            formData.append("is_published", updatedIsPublished);
            formData.append("author_name", updatedAuthorName);

            // Handle image upload logic
            if (updatedImage && typeof updatedImage !== "string") {
                // New image uploaded
                formData.append("thumbnail_url", updatedImage);
            } else if (updatedImage && typeof updatedImage === "string") {
                // Existing image path (when user didn't change the image)
                formData.append("existingImage", updatedImage);
            } else {
                // No image (user removed the image) - send empty string to remove image
                formData.append("existingImage", "");
            }

            await axios.put(`${base_url}/api/blogs/updateBlog/${selectedBlog.blog_id}`, formData, {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            });

            message.success("Blog updated successfully!");
            setIsModalOpen(false);

            // Refresh the blogs list
            const response = await axios.get(`${base_url}/api/blogs/getBlog`);
            setBlogs(response.data.blogs);
        } catch (error) {
            console.error("Error updating blog:", error);
            message.error("Failed to update blog.");
        }
    };

    // Handle Image Upload
    const handleImageUpload = ({ file }) => {
        setUpdatedImage(file);
    };

    // Handle Image Removal
    const handleRemoveImage = () => {
        setUpdatedImage(null); // Set to null to indicate no image
    };

    // Handle Delete Blog
    const handleDeleteBlog = async (blogId) => {
        console.log(`Attempting to delete blog with ID: ${blogId}`);  // Debugging log
        try {
            await axios.delete(`${base_url}/api/blogs/deleteBlog/${blogId}`);
            message.success("Blog deleted successfully!");
            setBlogs(blogs.filter((blog) => blog.blog_id !== blogId));
        } catch (error) {
            console.error("Error deleting blog:", error.response || error);
            message.error("Failed to delete blog.");
        }
    };

    // React Quill modules and formats
    const quillModules = {
        toolbar: [
            [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
            ['bold', 'italic', 'underline', 'strike'],
            [{ 'list': 'ordered' }, { 'list': 'bullet' }],
            [{ 'color': [] }, { 'background': [] }],
            [{ 'align': [] }],
            ['link', 'image'],
            ['clean']
        ],
        clipboard: {
            matchVisual: false,
        }
    };

    const quillFormats = [
        'header',
        'bold', 'italic', 'underline', 'strike',
        'list', 'bullet',
        'color', 'background',
        'align',
        'link', 'image'
    ];

    // Table columns
    const columns = [
        {
            title: "Image",
            dataIndex: "thumbnail_url",
            key: "thumbnail_url",
            render: (thumbnail_url) => (
                thumbnail_url ? (
                    <img
                        src={`${base_url}/${thumbnail_url}`}
                        alt="blog"
                        style={{
                            width: "50px",
                            height: "50px",
                            objectFit: "cover",
                            borderRadius: "4px",
                        }}
                    />
                ) : (
                    "No Image"
                )
            ),
        },
        {
            title: "Title",
            dataIndex: "title",
            key: "title",
        },
        {
            title: "Category",
            dataIndex: "category_name",
            key: "category_name",
        },
        {
            title: "Author",
            dataIndex: "author_name",
            key: "author_name",
        },
        {
            title: "Published",
            dataIndex: "is_published",
            key: "is_published",
            render: (is_published) => (
                <span style={{ color: is_published === "true" || is_published === true ? "green" : "red" }}>
                    {is_published === "true" || is_published === true ? "Yes" : "No"}
                </span>
            ),
        },
        {
            title: "Actions",
            key: "actions",
            render: (_, record) => (
                <div style={{ textAlign: "right" }}>
                    <BiEdit
                        className="action-icon"
                        style={{ cursor: "pointer", marginRight: "10px", fontSize: "18px" }}
                        onClick={() => handleEdit(record)}
                    />
                    <AiFillDelete
                        className="action-icon text-danger"
                        style={{ cursor: "pointer", fontSize: "18px" }}
                        onClick={() => handleDeleteBlog(record.blog_id)}
                    />
                </div>
            ),
        },
    ];

    // Table data source
    const dataSource = blogs.map((blog) => ({
        ...blog,
        key: blog.blog_id, // Use blog_id as the key for React
    }));

    return (
        <div>
            <h3 className="mb-4 title">Blog List</h3>
            <Table
                columns={columns}
                dataSource={dataSource}
                pagination={{ pageSize: 5 }}
                rowKey="blog_id"
            />

            {/* Edit Modal */}
            <Modal
                title="Edit Blog"
                visible={isModalOpen}
                onCancel={() => setIsModalOpen(false)}
                width={800}
                footer={[
                    <Button key="cancel" onClick={() => setIsModalOpen(false)}>
                        Cancel
                    </Button>,
                    <Button key="save" type="primary" onClick={handleSaveChanges}>
                        Save Changes
                    </Button>,
                ]}
            >
                <div style={{ maxHeight: "70vh", overflowY: "auto" }}>
                    <div style={{ marginBottom: "15px" }}>
                        <label style={{ display: "block", marginBottom: "5px", fontWeight: "bold" }}>Title:</label>
                        <Input
                            value={updatedTitle}
                            onChange={(e) => setUpdatedTitle(e.target.value)}
                            placeholder="Enter blog title"
                        />
                    </div>

                    <div style={{ marginBottom: "15px" }}>
                        <label style={{ display: "block", marginBottom: "5px", fontWeight: "bold" }}>Slug:</label>
                        <Input
                            value={updatedSlug}
                            onChange={(e) => setUpdatedSlug(e.target.value)}
                            placeholder="Enter blog slug"
                        />
                    </div>

                    <div style={{ marginBottom: "15px" }}>
                        <label style={{ display: "block", marginBottom: "5px", fontWeight: "bold" }}>Author Name:</label>
                        <Input
                            value={updatedAuthorName}
                            onChange={(e) => setUpdatedAuthorName(e.target.value)}
                            placeholder="Enter author name"
                        />
                    </div>

                    <div style={{ marginBottom: "15px" }}>
                        <label style={{ display: "block", marginBottom: "5px", fontWeight: "bold" }}>Category:</label>
                        <Select
                            value={updatedCategoryId}
                            onChange={setUpdatedCategoryId}
                            placeholder="Select category"
                            style={{ width: "100%" }}
                        >
                            {categories.map((category) => (
                                <Option key={category.category_id} value={category.category_id}>
                                    {category.name}
                                </Option>
                            ))}
                        </Select>
                    </div>

                    <div style={{ marginBottom: "15px" }}>
                        <label style={{ display: "block", marginBottom: "5px", fontWeight: "bold" }}>Content:</label>
                        <div>
                            <ReactQuill
                                key={`quill-${selectedBlog?.blog_id || 'new'}-${isModalOpen}`}
                                value={updatedContent}
                                onChange={(content) => {
                                    console.log("ReactQuill onChange:", content);
                                    setUpdatedContent(content);
                                }}
                                modules={quillModules}
                                formats={quillFormats}
                                style={{ height: "200px", marginBottom: "40px" }}
                                theme="snow"
                                placeholder="Enter blog content..."
                            />
                        </div>
                        {/* Debug info */}
                        <div style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
                            Content length: {updatedContent?.length || 0} characters
                        </div>
                        {/* React Quill CSS for proper formatting */}
                        <style>
                            {`
                                .ql-editor {
                                    font-family: 'Roboto', sans-serif;
                                    font-size: 16px;
                                    line-height: 1.8;
                                    color: #444;
                                }
                                .ql-editor h1 {
                                    font-size: 2em;
                                    margin: 20px 0 10px 0;
                                    font-weight: 600;
                                    color: #333;
                                }
                                .ql-editor h2 {
                                    font-size: 1.75em;
                                    margin: 20px 0 10px 0;
                                    font-weight: 600;
                                    color: #333;
                                }
                                .ql-editor h3 {
                                    font-size: 1.5em;
                                    margin: 20px 0 10px 0;
                                    font-weight: 600;
                                    color: #333;
                                }
                                .ql-editor h4 {
                                    font-size: 1.25em;
                                    margin: 20px 0 10px 0;
                                    font-weight: 600;
                                    color: #333;
                                }
                                .ql-editor h5 {
                                    font-size: 1.1em;
                                    margin: 20px 0 10px 0;
                                    font-weight: 600;
                                    color: #333;
                                }
                                .ql-editor h6 {
                                    font-size: 1em;
                                    margin: 20px 0 10px 0;
                                    font-weight: 600;
                                    color: #333;
                                }
                                .ql-editor p {
                                    margin: 16px 0;
                                    line-height: 1.8;
                                }
                                .ql-editor strong, .ql-editor b {
                                    font-weight: 600;
                                    color: #333;
                                }
                                .ql-editor em, .ql-editor i {
                                    font-style: italic;
                                }
                                .ql-editor ul, .ql-editor ol {
                                    margin: 16px 0;
                                    padding-left: 30px;
                                }
                                .ql-editor li {
                                    margin: 8px 0;
                                    line-height: 1.6;
                                }
                                .ql-editor a {
                                    color: #6B3F4F;
                                    text-decoration: underline;
                                }
                                .ql-editor a:hover {
                                    color: #8B5F6F;
                                }
                                .ql-editor blockquote {
                                    margin: 20px 0;
                                    padding: 15px 20px;
                                    border-left: 4px solid #6B3F4F;
                                    background-color: #f8f8f8;
                                    font-style: italic;
                                    color: #666;
                                }
                            `}
                        </style>
                    </div>

                    <div style={{ marginBottom: "15px" }}>
                        <label style={{ display: "block", marginBottom: "5px", fontWeight: "bold" }}>Tags:</label>
                        <Input
                            value={updatedTags}
                            onChange={(e) => setUpdatedTags(e.target.value)}
                            placeholder="Enter blog tags (separate by commas)"
                        />
                    </div>

                    <div style={{ marginBottom: "15px" }}>
                        <label style={{ display: "block", marginBottom: "5px", fontWeight: "bold" }}>Published:</label>
                        <Switch
                            checked={updatedIsPublished}
                            onChange={setUpdatedIsPublished}
                            checkedChildren="Yes"
                            unCheckedChildren="No"
                        />
                    </div>

                    <div style={{ marginBottom: "15px" }}>
                        <label style={{ display: "block", marginBottom: "5px", fontWeight: "bold" }}>Image:</label>
                        {updatedImage ? (
                            <div>
                                <img
                                    src={
                                        typeof updatedImage === "string"
                                            ? `${base_url}/${updatedImage}`
                                            : URL.createObjectURL(updatedImage)
                                    }
                                    alt="blog"
                                    style={{
                                        width: "100px",
                                        height: "100px",
                                        objectFit: "cover",
                                        borderRadius: "4px",
                                        marginBottom: "10px",
                                    }}
                                />
                                <br />
                                <Button type="link" danger onClick={handleRemoveImage}>
                                    Remove Image
                                </Button>
                            </div>
                        ) : (
                            <Upload
                                accept="image/*"
                                beforeUpload={() => false}
                                onChange={handleImageUpload}
                            >
                                <Button type="link">Upload Image</Button>
                            </Upload>
                        )}
                    </div>
                </div>
            </Modal>
        </div>
    );
};

export default BlogList;
